import React, { useState, useRef, useEffect } from 'react';
import { Smartphone, Download, Share2, Mail, Send, Check, Copy, Loader2 } from 'lucide-react';
import { useAppDemo } from '../../context/AppDemoContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import toast from 'react-hot-toast';
import { 
    captureAppScreenshot, 
    createAppDemo, 
    generateShareLink,
    saveAppDemoScreenshots,
    shareAppDemoViaEmail,
    generateOpenAIAppContent
} from '../../services/clientAppDemoService';
import AppPreview from '../mockup/AppPreview';

/**
 * App Prototype slide that displays the final app demo
 * and provides sharing options
 */
const AppPrototype = ({ organization }) => {
    const {
        appName,
        appDescription,
        primaryColor,
        secondaryColor,
        logoUrl,
        bookTopic,
        bookTitles,
        splashVideo,
        selectedFeatures,
        materialTheme,
        isLoading,
        setIsLoading,
        setLoadingMessage
    } = useAppDemo();

    const { trackEvent } = useAnalytics();
    const [activeTab, setActiveTab] = useState('prototype');
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [shareEmail, setShareEmail] = useState('');
    const [sharePhone, setSharePhone] = useState('');
    const [isSharing, setIsSharing] = useState(false);
    const [shareSuccess, setShareSuccess] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [appDemoId, setAppDemoId] = useState(null);
    const [shareLink, setShareLink] = useState('');
    const [currentScreen, setCurrentScreen] = useState('home');
    const [generatedScreens, setGeneratedScreens] = useState(null);
    const [generationInProgress, setGenerationInProgress] = useState(false);
    
    const prototypeRef = useRef(null);
    
    // Save app demo when first rendered and generate OpenAI content
    useEffect(() => {
        // Only save if we haven't already
        if (!appDemoId) {
            saveAppDemo();
        }
        
        // Generate custom content with OpenAI if we don't have it yet
        if (!generatedScreens && !generationInProgress) {
            generateAppContent();
        }
    }, []);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        trackEvent('app_prototype_tab_change', { tab });
    };

    const toggleShareOptions = () => {
        setShowShareOptions(!showShareOptions);
        
        if (!showShareOptions) {
            trackEvent('share_options_opened');
        }
    };

    const handleEmailChange = (e) => {
        setShareEmail(e.target.value);
    };

    const handlePhoneChange = (e) => {
        setSharePhone(e.target.value);
    };

    // Generate custom app content using OpenAI
    const generateAppContent = async () => {
        setGenerationInProgress(true);
        setIsLoading(true);
        setLoadingMessage('Generating custom app content with AI...');
        
        try {
            // Collect all user selections to send to OpenAI
            const appData = {
                appName: appName || 'BookMind',
                appDescription: appDescription || 'A personalized book app',
                primaryColor,
                secondaryColor,
                logoUrl,
                bookTopic,
                bookTitles,
                splashVideoId: splashVideo?.id,
                splashVideoTitle: splashVideo?.title,
                splashVideoThumbnail: splashVideo?.thumbnail,
                selectedFeatures: selectedFeatures || [],
                materialTheme,
                organizationName: organization?.name
            };
            
            // Call service to generate content with OpenAI
            const generatedContent = await generateOpenAIAppContent(appData);
            
            // Store generated screens
            setGeneratedScreens(generatedContent);
            
            // Track successful generation
            trackEvent('app_content_ai_generated', { 
                screenCount: generatedContent.screens?.length || 0 
            });
            
            toast.success('Custom app content generated!');
        } catch (error) {
            console.error('Error generating app content:', error);
            toast.error('Failed to generate custom content. Using default content instead.');
            
            // Set fallback content if generation fails
            setGeneratedScreens({
                screens: [
                    { id: 'home', name: 'Home' },
                    { id: 'library', name: 'Library' },
                    { id: 'book', name: 'Book Detail' },
                    { id: 'profile', name: 'Profile' }
                ],
                welcomeMessage: `Welcome to ${appName || 'BookMind'}`,
                description: appDescription || 'Your personalized book experience'
            });
        } finally {
            setGenerationInProgress(false);
            setIsLoading(false);
        }
    };

    // Save app demo to database
    const saveAppDemo = async () => {
        setIsLoading(true);
        setLoadingMessage('Saving your app demo...');
        
        try {
            const config = {
                name: appName || 'My Book App',
                description: appDescription || 'A custom book app',
                primaryColor,
                secondaryColor,
                logoUrl,
                bookTopic,
                splashVideoId: splashVideo?.id,
                selectedFeatures: selectedFeatures || []
            };
            
            const appDemo = await createAppDemo(config);
            setAppDemoId(appDemo.id);
            setShareLink(generateShareLink(appDemo.id));
            
            // Track creation
            trackEvent('app_demo_created', { appDemoId: appDemo.id });
            
            toast.success('App demo saved successfully!');
            
            // Capture screenshots after creation
            await captureScreenshots(appDemo.id);
        } catch (error) {
            console.error('Error saving app demo:', error);
            toast.error('Failed to save app demo. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Capture screenshots of the app
    const captureScreenshots = async (demoId) => {
        if (!prototypeRef.current) return;
        
        setIsLoading(true);
        setLoadingMessage('Capturing screenshots...');
        
        try {
            // Capture main screenshot
            const screenshot = await captureAppScreenshot(prototypeRef.current);
            
            // Save screenshots to app demo
            await saveAppDemoScreenshots(demoId, [screenshot]);
            
            // Track screenshot capture
            trackEvent('app_screenshots_captured', { count: 1 });
        } catch (error) {
            console.error('Error capturing screenshots:', error);
            toast.error('Failed to capture screenshots');
        } finally {
            setIsLoading(false);
        }
    };

    const downloadScreenshot = async () => {
        if (!prototypeRef.current) return;
        
        setIsLoading(true);
        setLoadingMessage('Capturing screenshot...');
        
        try {
            // Capture screenshot
            const screenshot = await captureAppScreenshot(prototypeRef.current);
            
            // Create download link
            const a = document.createElement('a');
            a.href = screenshot;
            a.download = `${appName || 'App'}-Screenshot.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            // Track screenshot download
            trackEvent('screenshot_downloaded');
            
            toast.success('Screenshot downloaded');
        } catch (error) {
            console.error('Error downloading screenshot:', error);
            toast.error('Failed to download screenshot');
        } finally {
            setIsLoading(false);
        }
    };

    const copyLinkToClipboard = () => {
        if (!shareLink) return;
        
        navigator.clipboard.writeText(shareLink).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 3000);
            
            // Track link copy
            trackEvent('share_link_copied');
            
            toast.success('Link copied to clipboard');
        }).catch(error => {
            console.error('Error copying link:', error);
            toast.error('Failed to copy link');
        });
    };

    const handleShareSubmit = async (e) => {
        e.preventDefault();
        if (!appDemoId) return;
        
        setIsSharing(true);
        setIsLoading(true);
        setLoadingMessage('Sending app link...');
        
        try {
            // Share via email
            if (shareEmail) {
                await shareAppDemoViaEmail(appDemoId, shareEmail);
                trackEvent('app_shared_via_email', { recipient: shareEmail });
            }
            
            // For SMS sharing, we would need a separate endpoint
            // Placeholder for now
            if (sharePhone) {
                trackEvent('app_shared_via_sms', { recipient: sharePhone });
                // await shareAppDemoViaSms(appDemoId, sharePhone);
            }
            
            setShareSuccess(true);
            setTimeout(() => {
                setShareSuccess(false);
                setShowShareOptions(false);
            }, 3000);
            
            toast.success('App demo shared successfully!');
        } catch (error) {
            console.error('Error sharing app:', error);
            toast.error('Failed to share app demo');
        } finally {
            setIsSharing(false);
            setIsLoading(false);
        }
    };

    // Handle screen navigation within the app prototype
    const navigateToScreen = (screenId) => {
        setCurrentScreen(screenId);
        trackEvent('app_prototype_screen_change', { screen: screenId });
    };

    // Render the app prototype mockup with OpenAI generated content
    const renderAppPrototype = () => {
        if (generationInProgress) {
            return (
                <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-12 h-12 text-green-500 animate-spin mb-4" />
                    <p className="text-gray-600">Generating your custom app content...</p>
                </div>
            );
        }
        
        return (
            <div ref={prototypeRef}>
                <AppPreview 
                    appName={appName || 'BookMind'}
                    logoUrl={logoUrl}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    bookTitles={bookTitles || []}
                    currentScreen={currentScreen}
                    screens={generatedScreens?.screens || []}
                    welcomeMessage={generatedScreens?.welcomeMessage}
                    appDescription={generatedScreens?.description || appDescription}
                    splashVideo={splashVideo}
                    features={selectedFeatures}
                    materialTheme={materialTheme}
                    navigateToScreen={navigateToScreen}
                />
            </div>
        );
    };

    // Render sharing options
    const renderShareOptions = () => {
        return (
            <div className="max-w-md mx-auto">
                <div className="backdrop-blur-md bg-white/90 dark:bg-gray-800/90 rounded-lg shadow-md p-6 mb-4">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">Share Your App Demo</h3>
                    
                    <div className="mb-6">
                        <div className="flex">
                            <input
                                type="text"
                                readOnly
                                value={shareLink || 'Generating link...'}
                                className="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-gray-50 dark:bg-gray-700 text-sm dark:text-gray-200"
                            />
                            <button
                                onClick={copyLinkToClipboard}
                                disabled={!shareLink}
                                className={`
                                    px-3 py-2 rounded-r-md text-sm font-medium
                                    ${linkCopied 
                                        ? 'bg-green-600 text-white' 
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500'}
                                    ${!shareLink ? 'opacity-50 cursor-not-allowed' : ''}
                                `}
                            >
                                {linkCopied ? (
                                    <span className="flex items-center">
                                        <Check className="w-4 h-4 mr-1" />
                                        Copied
                                    </span>
                                ) : (
                                    <span className="flex items-center">
                                        <Copy className="w-4 h-4 mr-1" />
                                        Copy
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                    
                    <form onSubmit={handleShareSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Send via Email
                            </label>
                            <div className="flex">
                                <input
                                    type="email"
                                    id="email"
                                    value={shareEmail}
                                    onChange={handleEmailChange}
                                    className="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md text-sm dark:bg-gray-700 dark:text-gray-200"
                                    placeholder="Enter email address"
                                />
                                <button
                                    type="button"
                                    onClick={handleShareSubmit}
                                    disabled={!shareEmail || isSharing || !appDemoId}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-r-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <Mail className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Send via SMS
                            </label>
                            <div className="flex">
                                <input
                                    type="tel"
                                    id="phone"
                                    value={sharePhone}
                                    onChange={handlePhoneChange}
                                    className="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md text-sm dark:bg-gray-700 dark:text-gray-200"
                                    placeholder="Enter phone number"
                                />
                                <button
                                    type="button"
                                    onClick={handleShareSubmit}
                                    disabled={!sharePhone || isSharing || !appDemoId}
                                    className="px-3 py-2 bg-green-600 text-white rounded-r-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        
                        {shareSuccess && (
                            <div className="bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-800 rounded-md p-3 text-green-800 dark:text-green-300 text-sm">
                                <div className="flex items-center">
                                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                                    Link successfully shared!
                                </div>
                            </div>
                        )}
                    </form>
                </div>
                
                <div className="text-center">
                    <button
                        type="button"
                        onClick={toggleShareOptions}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
                    >
                        Back to App Preview
                    </button>
                </div>
            </div>
        );
    };

    // Render feature list with selected features highlighted
    const renderFeaturesList = () => {
        const allFeatures = [
            { id: 'branding', name: 'Branded Experience', description: 'Custom colors, logos, and styling that match your brand identity.' },
            { id: 'content', name: 'Content Library', description: `${bookTitles?.length || 0} books on ${bookTopic || 'selected topic'}.` },
            { id: 'multimedia', name: 'Multimedia Support', description: 'Video splash screen and media integration.' },
            { id: 'accounts', name: 'User Accounts', description: 'Personalized profiles and content recommendations.' },
            { id: 'search', name: 'Search Capability', description: 'Find books and content with powerful search.' },
            { id: 'analytics', name: 'Analytics Dashboard', description: 'Track user engagement and content performance.' },
            { id: 'offline', name: 'Offline Reading', description: 'Download content for offline access.' },
            { id: 'social', name: 'Social Sharing', description: 'Share content on social media platforms.' },
            { id: 'notifications', name: 'Push Notifications', description: 'Send updates and recommendations to users.' }
        ];
        
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
                    {appName || 'App'} Features
                </h3>
                
                <ul className="space-y-3">
                    {allFeatures.map(feature => {
                        const isSelected = selectedFeatures?.includes(feature.id);
                        return (
                            <li key={feature.id} className="flex items-start">
                                <div 
                                    className={`flex-shrink-0 w-5 h-5 rounded-full mr-2 mt-0.5 ${isSelected ? '' : 'opacity-40'}`}
                                    style={{ backgroundColor: isSelected ? primaryColor : '#cccccc' }}
                                />
                                <p className={`text-gray-700 dark:text-gray-300 ${isSelected ? 'font-normal' : 'opacity-70'}`}>
                                    <span className="font-medium">{feature.name}:</span> {feature.description}
                                </p>
                            </li>
                        );
                    })}
                </ul>
                
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Next Steps</h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Like what you see? We can develop a fully functional version of this app for your organization.
                    </p>
                    <button
                        onClick={toggleShareOptions}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share App Demo
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
                <div 
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                    style={{ backgroundColor: primaryColor || 'rgba(100, 100, 100, 0.5)' }}
                >
                    <Smartphone className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {appName || 'Your App'} Prototype
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    Here's your personalized app with AI-generated content. Interact with it below.
                </p>
            </div>

            {/* Tab Navigation */}
            {!showShareOptions && (
                <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                    <button
                        onClick={() => handleTabChange('prototype')}
                        className={`
                            flex-1 py-3 font-medium text-sm border-b-2 
                            ${activeTab === 'prototype' 
                                ? 'border-current text-current' 
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}
                        `}
                        style={activeTab === 'prototype' ? { color: primaryColor, borderColor: primaryColor } : {}}
                    >
                        App Preview
                    </button>
                    <button
                        onClick={() => handleTabChange('features')}
                        className={`
                            flex-1 py-3 font-medium text-sm border-b-2 
                            ${activeTab === 'features' 
                                ? 'border-current text-current'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}
                        `}
                        style={activeTab === 'features' ? { color: primaryColor, borderColor: primaryColor } : {}}
                    >
                        Features
                    </button>
                </div>
            )}

            {/* Share Options or App Content */}
            {showShareOptions ? (
                renderShareOptions()
            ) : (
                <div>
                    {activeTab === 'prototype' && (
                        <div className="text-center">
                            {renderAppPrototype()}
                            
                            <div className="flex justify-center mt-6 space-x-3">
                                <button
                                    onClick={downloadScreenshot}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download Screenshot
                                </button>
                                <button
                                    onClick={toggleShareOptions}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <Share2 className="w-4 h-4 mr-2" />
                                    Share App
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'features' && renderFeaturesList()}
                </div>
            )}
        </div>
    );
};

export default AppPrototype; 